import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAdminCompaniesApi } from '../api';
import {
  getCurrentCompanyId,
  selectCurrentAdminCompany,
  selectCurrentAdminCompanyLoading,
  setCurrentAdminCompany,
  setCurrentAdminCompanyLoading,
} from '../slices';

// Dedup concurrent fetches across the multiple useCurrentCompany consumers that
// mount together (Header, page controllers, AwayMessageSwitcher, ...). Without
// this, every instance fires GET /companies/{id} on first mount before redux is
// populated, causing N identical requests.
const inFlightCompanyIds = new Set<string>();

export function useCurrentCompany() {
  const dispatch = useDispatch();
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const company = useSelector(selectCurrentAdminCompany);
  const loading = useSelector(selectCurrentAdminCompanyLoading);
  const { getAdminCompany } = useAdminCompaniesApi();

  const fetchCompany = useCallback(
    async (companyId: string) => {
      dispatch(setCurrentAdminCompanyLoading(true));
      try {
        const response = await getAdminCompany(companyId);
        dispatch(setCurrentAdminCompany(response.data));
        localStorage.setItem(
          'bhiSandboxMode',
          (response.data.sandBoxMode || false).toString(),
        );
        return response.data;
      } finally {
        dispatch(setCurrentAdminCompanyLoading(false));
      }
    },
    [getAdminCompany, dispatch],
  );

  useEffect(() => {
    if (!selectedCompanyId) {
      dispatch(setCurrentAdminCompany(null));
      return;
    }

    if (company?.id !== selectedCompanyId && !inFlightCompanyIds.has(selectedCompanyId)) {
      inFlightCompanyIds.add(selectedCompanyId);
      fetchCompany(selectedCompanyId).finally(() => {
        inFlightCompanyIds.delete(selectedCompanyId);
      });
    }
  }, [selectedCompanyId, company?.id, fetchCompany]);

  const refetch = useCallback(async () => {
    if (!selectedCompanyId) return null;
    return fetchCompany(selectedCompanyId);
  }, [selectedCompanyId, fetchCompany]);

  return { company, loading, refetch };
}
